import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { fellasMarkerIcon } from './fellasIcon';

export interface Coords {
  lat: number;
  lng: number;
}

interface LeafletMapViewProps {
  center: Coords;
  zoom?: number;
  markerPosition?: Coords | null;
  draggable?: boolean;
  onDragEnd?: (coords: Coords) => void;
  onMapClick?: (coords: Coords) => void;
  circle?: {
    center: Coords;
    radius: number; // in km
  } | null;
  secondaryMarker?: {
    position: Coords;
    icon?: L.DivIcon;
  } | null;
  className?: string;
}

// Controller to handle view updates and invalidateSize on container resize/show
function MapController({ center, zoom }: { center: Coords; zoom: number }) {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 150);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (center && typeof center.lat === 'number' && typeof center.lng === 'number' && !isNaN(center.lat) && !isNaN(center.lng)) {
      map.setView([center.lat, center.lng], zoom, { animate: true });
    }
  }, [center?.lat, center?.lng, zoom, map]);

  return null;
}

// Handle map click events
function MapEventsHandler({ onMapClick }: { onMapClick?: (coords: Coords) => void }) {
  useMapEvents({
    click(e) {
      if (onMapClick) {
        onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    },
  });
  return null;
}

export const LeafletMapView: React.FC<LeafletMapViewProps> = ({
  center,
  zoom = 15,
  markerPosition,
  draggable = true,
  onDragEnd,
  onMapClick,
  circle,
  secondaryMarker,
  className = 'h-full w-full rounded-sm overflow-hidden',
}) => {
  const markerRef = useRef<L.Marker | null>(null);

  // Validate coordinates with fallback to Jakarta
  const safeLat = typeof center?.lat === 'number' && !isNaN(center.lat) ? center.lat : -6.2088;
  const safeLng = typeof center?.lng === 'number' && !isNaN(center.lng) ? center.lng : 106.8456;
  const safeCenter: [number, number] = [safeLat, safeLng];

  const markerLat = markerPosition && typeof markerPosition.lat === 'number' && !isNaN(markerPosition.lat)
    ? markerPosition.lat
    : safeLat;
  const markerLng = markerPosition && typeof markerPosition.lng === 'number' && !isNaN(markerPosition.lng)
    ? markerPosition.lng
    : safeLng;

  return (
    <div className={className} style={{ position: 'relative', width: '100%', height: '100%', minHeight: '180px' }}>
      <MapContainer
        center={safeCenter}
        zoom={zoom}
        scrollWheelZoom={true}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />

        <MapController center={{ lat: safeLat, lng: safeLng }} zoom={zoom} />
        <MapEventsHandler onMapClick={onMapClick} />

        {markerPosition !== null && (
          <Marker
            ref={markerRef}
            key={`fellas-pin-${markerLat}-${markerLng}`}
            position={[markerLat, markerLng]}
            icon={fellasMarkerIcon}
            draggable={draggable}
            eventHandlers={{
              dragend(e) {
                const marker = e.target;
                if (marker && onDragEnd) {
                  const latLng = marker.getLatLng();
                  onDragEnd({ lat: latLng.lat, lng: latLng.lng });
                }
              },
            }}
          />
        )}

        {secondaryMarker?.position && (
          <Marker
            key={`sec-marker-${secondaryMarker.position.lat}-${secondaryMarker.position.lng}`}
            position={[secondaryMarker.position.lat, secondaryMarker.position.lng]}
            icon={secondaryMarker.icon || fellasMarkerIcon}
            draggable={false}
          />
        )}

        {circle?.center && (
          <Circle
            center={[circle.center.lat, circle.center.lng]}
            radius={(circle.radius || 0.1) * 1000} // Convert km to meters
            pathOptions={{
              color: '#FF007F',
              fillColor: '#FFB6C1',
              fillOpacity: 0.25,
              weight: 2,
            }}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default LeafletMapView;
